export interface ScreenshotOptions {
  size?: [number, number]
  backgroundFill?: string
  compressionLevel?: number
}

export const captureBrowserScreenshot = async (options: ScreenshotOptions): Promise<string | null> => {
  try {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
      console.warn('Screen Capture API not available for browser screenshot')
      return null
    }

    const captureStream = await startCapture({
      video: {
        displaySurface: 'browser',
        ...(options.size
          ? {
              width: { ideal: options.size[0] },
              height: { ideal: options.size[1] },
            }
          : {}),
      },
      audio: false,
      preferCurrentTab: true,
      systemAudio: 'exclude',
      surfaceSwitching: 'exclude',
      monitorTypeSurfaces: 'exclude',
    } as any)

    if (!captureStream) {
      console.warn('Failed to start screen capture')
      return null
    }

    const video = document.createElement('video')
    video.srcObject = captureStream
    video.muted = true
    video.play()

    return new Promise((resolve) => {
      video.onloadedmetadata = () => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')

        if (!ctx) {
          console.warn('Failed to get canvas context')
          captureStream.getTracks().forEach((track) => track.stop())
          video.remove()
          resolve(null)
          return
        }

        if (options.size) {
          canvas.width = options.size[0]
          canvas.height = options.size[1]
        } else {
          canvas.width = video.videoWidth
          canvas.height = video.videoHeight
        }

        if (options.backgroundFill) {
          ctx.fillStyle = options.backgroundFill
          ctx.fillRect(0, 0, canvas.width, canvas.height)
        }

        if (options.size) {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
        } else {
          ctx.drawImage(video, 0, 0)
        }

        const dataUrl = canvas.toDataURL('image/jpeg', options.compressionLevel || 0.8)

        const base64Data = dataUrl.split(',')[1]

        captureStream.getTracks().forEach((track) => track.stop())
        video.remove()

        resolve(base64Data)
      }

      video.onerror = () => {
        console.warn('Video failed to load')
        captureStream.getTracks().forEach((track) => track.stop())
        video.remove()
        resolve(null)
      }
    })
  } catch (error) {
    console.error('Failed to capture browser screenshot:', error)
    return null
  }
}

async function startCapture(displayMediaOptions: DisplayMediaStreamOptions): Promise<MediaStream | null> {
  let captureStream: MediaStream | null = null

  try {
    captureStream = await navigator.mediaDevices.getDisplayMedia(displayMediaOptions)
  } catch (err) {
    console.error(`Error starting screen capture: ${err}`)
  }

  return captureStream
}
